import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { actors } from "@/mocks/actors";

interface SearchBoxProps {
  variant?: "light" | "dark";
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export default function SearchBox({
  variant = "light",
  className = "",
  placeholder = "搜索电影、演员或关键词",
  autoFocus = false,
}: SearchBoxProps) {
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const query = keyword.trim().toLowerCase();
  const movieHits = query
    ? movies
        .filter(
          (movie) =>
            movie.title.toLowerCase().includes(query) ||
            movie.englishTitle.toLowerCase().includes(query) ||
            movie.genres.some((genre) => genre.toLowerCase().includes(query)) ||
            movie.tags.some((tag) => tag.toLowerCase().includes(query)) ||
            movie.director.toLowerCase().includes(query),
        )
        .slice(0, 4)
    : [];
  const actorHits = query
    ? actors
        .filter(
          (actor) =>
            actor.name.toLowerCase().includes(query) ||
            actor.englishName.toLowerCase().includes(query),
        )
        .slice(0, 3)
    : [];
  const hasResult = movieHits.length > 0 || actorHits.length > 0;

  const goSearch = () => {
    if (!query) return;
    navigate(`/category?q=${encodeURIComponent(keyword.trim())}`);
    setOpen(false);
  };

  const isDark = variant === "dark";

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          goSearch();
        }}
        className={`flex items-center gap-2 rounded-full border px-3.5 py-2 transition-colors ${
          isDark
            ? "border-background-50/30 bg-foreground-950/25 text-background-50 focus-within:border-background-50/70"
            : "border-background-300 bg-background-50 text-foreground-800 focus-within:border-primary-400"
        }`}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`ri-search-line text-sm ${isDark ? "text-background-100/80" : "text-foreground-400"}`} />
        </span>
        <input
          type="text"
          value={keyword}
          autoFocus={autoFocus}
          onChange={(event) => {
            setKeyword(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          aria-label="站内搜索"
          className={`w-full min-w-0 bg-transparent text-sm outline-none ${
            isDark
              ? "text-background-50 placeholder:text-background-100/60"
              : "text-foreground-900 placeholder:text-foreground-400"
          }`}
        />
        {keyword && (
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              setOpen(false);
            }}
            className={`flex h-4 w-4 items-center justify-center ${isDark ? "text-background-100/70" : "text-foreground-400"}`}
            aria-label="清空搜索"
          >
            <i className="ri-close-circle-fill text-sm" />
          </button>
        )}
      </form>

      {open && query.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {hasResult ? (
            <div className="max-h-[380px] overflow-y-auto py-2">
              {movieHits.length > 0 && (
                <div className="px-2">
                  <p className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground-400">
                    影片
                  </p>
                  {movieHits.map((movie) => (
                    <button
                      key={movie.id}
                      type="button"
                      onClick={() => {
                        navigate(`/detail/${movie.id}`);
                        setOpen(false);
                        setKeyword("");
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="h-11 w-8 shrink-0 overflow-hidden rounded-sm bg-background-200">
                        <img
                          src={movie.cover}
                          alt={movie.title}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground-900">{movie.title}</span>
                        <span className="block text-[11px] text-foreground-500">
                          {movie.year} · {movie.genres.join(" / ")}
                        </span>
                      </span>
                      <i className="ri-arrow-right-up-line text-foreground-300" />
                    </button>
                  ))}
                </div>
              )}
              {actorHits.length > 0 && (
                <div className="mt-1 px-2">
                  <p className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-foreground-400">
                    演员
                  </p>
                  {actorHits.map((actor) => (
                    <button
                      key={actor.id}
                      type="button"
                      onClick={() => {
                        navigate(`/actors/${actor.id}`);
                        setOpen(false);
                        setKeyword("");
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full bg-background-200">
                        <img
                          src={actor.avatar}
                          alt={actor.name}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-900">
                        {actor.name}
                      </span>
                      <span className="text-[11px] text-foreground-400">{actor.englishName}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={goSearch}
                className="mt-1 flex w-full items-center justify-between border-t border-background-200 px-4 py-2.5 text-xs text-accent-600 transition-colors hover:bg-background-100"
              >
                查看「{keyword.trim()}」的全部搜索结果
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 px-4 py-8 text-center">
              <i className="ri-search-eye-line text-xl text-foreground-300" />
              <p className="text-sm text-foreground-700">没有找到相关结果</p>
              <p className="text-xs text-foreground-400">试试片名、演员姓名或「青春」「都市」等类型词</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}